import { useState } from 'react';
import { C, mono } from '../theme';
import { Icon, Mark } from '../icons';
import { Phone } from '../components/Phone';
import { Pill } from '../components/Pill';
import { MonthStepper } from '../components/MonthStepper';
import { LedgerFilter } from '../components/LedgerFilter';
import { Register } from '../components/Register';
import { Loading, Empty } from '../components/States';
import { useToast } from '../components/Toast';
import { api, ApiError } from '../api';
import type { LedgerScope, Me, Overview, Property, TenancyDetail, TenancyLedger } from '../api';
import { useAsync } from '../useAsync';
import { useRoute, type Route } from '../useRoute';
import { inr, currentPeriod, fmtDate } from '../format';
import { openWhatsApp, rentReminder } from '../whatsapp';
import { buildRegister } from '../register';
import { Row } from '../tenant/Row';
import { rowPill, dateCount } from './helpers';
import { ScopeChip } from './ScopeChip';
import { RoomsPage } from './pages/RoomsPage';
import { LedgerPage } from './pages/LedgerPage';
import { CollectionsPage } from './pages/CollectionsPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { MaintenancePage } from './pages/MaintenancePage';
import { TenancyDocuments, TenancyDates, TenancyRequests } from './tenancy/TenancyPanels';
import { RemindersPage } from './pages/RemindersPage';
import { SettingsPage } from './pages/SettingsPage';
import { AddTenancy } from './forms/AddTenancy';
import { RecordPayment } from './forms/RecordPayment';
import { EditTenancy } from './forms/EditTenancy';
import { InviteTenant } from './forms/InviteTenant';

const TABS = [
  ['overview', 'grid', 'Overview'],
  ['list', 'key', 'Tenancies'],
  ['ledger', 'ledger', 'Ledger'],
  ['more', 'more', 'More'],
] as const;

type Screen = 'overview' | 'list' | 'detail' | 'rooms' | 'ledger' | 'collections' | 'more' | 'documents' | 'maintenance' | 'reminders' | 'settings';

const MORE_ITEMS: Array<[Screen, string, string, string]> = [
  ['rooms', 'home', 'Rooms', 'Inventory & rent'],
  ['collections', 'rupee', 'Collections', 'Reliability & nudges'],
  ['documents', 'doc', 'Documents', 'Leases, IDs, receipts'],
  ['maintenance', 'wrench', 'Maintenance', 'Tenant requests'],
  ['reminders', 'bell', 'Reminders', 'Renewals & expiries'],
  ['settings', 'gear', 'Settings', 'Property & account'],
];

const MORE_SCREENS: Screen[] = ['more', 'rooms', 'collections', 'documents', 'maintenance', 'reminders', 'settings'];

const SCREEN_TITLE: Partial<Record<Screen, string>> = {
  detail: 'Tenancy',
  rooms: 'Rooms',
  ledger: 'Ledger',
  collections: 'Collections',
  more: 'More',
  documents: 'Documents',
  maintenance: 'Maintenance',
  reminders: 'Reminders',
  settings: 'Settings',
};

/** Map the URL route → the mobile screen + selected tenancy. */
function screenFromRoute(route: Route): { screen: Screen; tenancyId: string | null } {
  switch (route.section) {
    case 'tenancies':
      return route.id ? { screen: 'detail', tenancyId: route.id } : { screen: 'list', tenancyId: null };
    case 'rooms':
    case 'ledger':
    case 'collections':
    case 'more':
    case 'documents':
    case 'maintenance':
    case 'reminders':
    case 'settings':
      return { screen: route.section, tenancyId: null };
    default:
      return { screen: 'overview', tenancyId: null };
  }
}

/** Route path for a bottom-tab key (the "Tenancies" tab lives at #/tenancies). */
function tabPath(key: string): string {
  return key === 'list' ? '#/tenancies' : `#/${key}`;
}

export function OwnerMobile({ me, onLogout, onMeChange }: { me: Me; onLogout: () => void; onMeChange?: (me: Me) => void }) {
  const { route, navigate, back } = useRoute();
  const { screen, tenancyId } = screenFromRoute(route);

  const { data: properties } = useAsync<Property[]>(() => api.properties(), []);
  const [propertyEdit, setPropertyEdit] = useState<Property | null>(null);
  const property = propertyEdit ?? properties?.[0] ?? null;
  const [period, setPeriod] = useState<string | null>(null); // null → server's current month
  const overview = useAsync<Overview>(
    () => (property ? api.overview(property.id, period ?? undefined) : Promise.reject(new Error('no property'))),
    [property?.id, period],
  );

  const open = (id: string) => navigate(`#/tenancies/${id}`);

  const tabActive = (key: string) => {
    if (key === 'list') return screen === 'list' || screen === 'detail';
    if (key === 'more') return MORE_SCREENS.includes(screen);
    return screen === key;
  };

  const hasBack = screen === 'detail' || (MORE_SCREENS.includes(screen) && screen !== 'more');

  return (
    <Phone>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* top bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 18px 12px' }}>
          {hasBack ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <button onClick={() => back()} aria-label="Back" style={{ border: 0, background: 'transparent', cursor: 'pointer', color: C.ink2, display: 'flex', padding: 0 }}>
                <Icon n="chev" size={18} style={{ transform: 'scaleX(-1)' }} />
              </button>
              <span style={{ fontWeight: 600, color: C.ink, fontSize: 16 }}>{SCREEN_TITLE[screen] ?? 'Premise'}</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <Mark size={26} />
              <span style={{ fontWeight: 600, color: C.ink, fontSize: 16 }}>Premise</span>
            </div>
          )}
          <button onClick={onLogout} aria-label="Log out" style={{ border: 0, background: 'transparent', color: C.ink2, cursor: 'pointer', display: 'flex', padding: 2 }}>
            <Icon n="logout" size={18} />
          </button>
        </div>

        {/* property-scope chip (groundwork for a future switcher) */}
        <div style={{ padding: '0 18px 10px' }}>
          <ScopeChip name={property?.name} />
        </div>

        <div className="premise-scroll" style={{ flex: 1, overflowY: 'auto', padding: '4px 16px 16px', display: 'flex', flexDirection: 'column', gap: 13 }}>
          {screen === 'overview' && <MobileOverview state={overview} propertyName={property?.name} period={period} onPeriod={setPeriod} />}
          {screen === 'list' && <MobileList state={overview} onOpen={open} />}
          {screen === 'detail' && tenancyId && <MobileDetail tenancyId={tenancyId} />}
          {screen === 'rooms' && <RoomsPage propertyId={property?.id} propertyName={property?.name} onOpen={open} />}
          {screen === 'ledger' && <LedgerPage propertyId={property?.id} propertyName={property?.name} onOpen={open} />}
          {screen === 'collections' && <CollectionsPage propertyName={property?.name} />}
          {screen === 'more' && <MoreMenu onGo={(s) => navigate(`#/${s}`)} />}
          {screen === 'documents' && <DocumentsPage propertyId={property?.id} propertyName={property?.name} />}
          {screen === 'maintenance' && <MaintenancePage />}
          {screen === 'reminders' && <RemindersPage propertyId={property?.id} propertyName={property?.name} />}
          {screen === 'settings' && <SettingsPage me={me} property={property} onLogout={onLogout} onMeChange={onMeChange} onPropertyChange={setPropertyEdit} />}
        </div>

        <div style={{ display: 'flex', borderTop: `1px solid ${C.line}`, background: C.card }}>
          {TABS.map(([key, icon, labelText]) => (
            <button
              key={key}
              onClick={() => navigate(tabPath(key))}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '9px 0 11px', color: tabActive(key) ? C.accent : C.muted, background: 'transparent', border: 0, fontSize: 10.4, fontWeight: 600, cursor: 'pointer' }}
            >
              <Icon n={icon} size={18} /> {labelText}
            </button>
          ))}
        </div>
      </div>
    </Phone>
  );
}

/** The "More" tab — a menu into the secondary owner surfaces. */
function MoreMenu({ onGo }: { onGo: (s: Screen) => void }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, overflow: 'hidden' }}>
      {MORE_ITEMS.map(([key, icon, title, sub], i) => (
        <button
          key={key}
          onClick={() => onGo(key)}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '13px 15px', borderTop: i ? `1px solid ${C.line2}` : 0, background: 'transparent', border: 0, cursor: 'pointer', textAlign: 'left' }}
        >
          <span style={{ width: 34, height: 34, borderRadius: 9, background: C.soft, color: C.accent2, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
            <Icon n={icon} size={17} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <b style={{ fontSize: 14, color: C.ink, fontWeight: 600, display: 'block' }}>{title}</b>
            <small style={{ fontSize: 12, color: C.muted }}>{sub}</small>
          </div>
          <Icon n="chev" size={16} color={C.muted} />
        </button>
      ))}
    </div>
  );
}

/* --- panels --- */
function MobileOverview({
  state,
  propertyName,
  period,
  onPeriod,
}: {
  state: ReturnType<typeof useAsync<Overview>>;
  propertyName?: string;
  period: string | null;
  onPeriod: (p: string) => void;
}) {
  if (state.loading && !state.data) return <Loading label="Loading…" minHeight={300} />;
  if (state.error || !state.data)
    return (
      <Empty icon="grid" title="Overview unavailable" minHeight={300}>
        <button onClick={state.reload} style={retryBtn}>Retry</button>
      </Empty>
    );
  const notify = useToast();
  const { stats, dates, tenancies, arrears } = state.data;
  const expected = stats?.expected ?? 0;
  const collected = stats?.collected ?? 0;
  const pct = expected > 0 ? Math.min(100, Math.round((collected / expected) * 100)) : 0;
  const shownPeriod = period ?? state.data.period ?? currentPeriod();
  const toSend = tenancies
    .filter((t) => (t.lifecycle ?? 'active') === 'active' && (t.balance ?? 0) > 0 && (t.status === 'overdue' || (t.dueInDays ?? 99) <= 5))
    .sort((a, b) => (a.dueInDays ?? 0) - (b.dueInDays ?? 0));

  function nudge(t: (typeof tenancies)[number]) {
    const di = t.dueInDays ?? 0;
    const msg = rentReminder({
      property: propertyName ?? 'your property',
      tenant: t.tenantName,
      amount: inr(t.balance ?? t.netExpected ?? t.rentAmount),
      dueLabel: t.dueDate ? fmtDate(t.dueDate) : `the ${t.dueDay ?? 5}th`,
      overdue: di < 0 || t.status === 'overdue',
    });
    if (!openWhatsApp(t.tenantPhone, msg)) notify('Add the tenant’s phone (Invite tenant) to send on WhatsApp');
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 12.5, color: C.muted }}>
          Command centre
          <b style={{ color: C.ink, fontSize: 15, display: 'block', fontWeight: 650, marginTop: 1 }}>{propertyName ?? 'Property'}</b>
        </div>
        <MonthStepper period={shownPeriod} onChange={onPeriod} compact />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
        <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, padding: '11px 13px' }}>
          <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>Collected</div>
          <div style={{ ...mono(), fontSize: 19, fontWeight: 650, color: C.ink, marginTop: 3 }}>{inr(collected)}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>of {inr(expected)} · <b style={{ color: pct >= 100 ? C.hi : C.ink2 }}>{pct}%</b></div>
          <div style={{ height: 6, borderRadius: 99, background: C.line2, overflow: 'hidden', marginTop: 8 }}><span style={{ display: 'block', height: '100%', width: `${pct}%`, background: C.hi }} /></div>
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, padding: '11px 13px' }}>
          <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>Arrears</div>
          <div style={{ ...mono(), fontSize: 19, fontWeight: 650, color: (arrears?.total ?? 0) > 0 ? C.red : C.hi, marginTop: 3 }}>{inr(arrears?.total ?? 0)}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{stats?.occupied ?? 0}/{stats?.units ?? 0} units occupied</div>
          <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 16, marginTop: 6 }}>
            {[arrears?.b0_30 ?? 0, arrears?.b31_60 ?? 0, arrears?.b61_90 ?? 0, arrears?.b90 ?? 0].map((v, i, all) => {
              const max = Math.max(1, ...all); const cols = [C.greenSoft, C.amberSoft, C.redSoft, C.red];
              return <div key={i} style={{ flex: 1, height: `${Math.max(12, (v / max) * 100)}%`, background: cols[i], borderRadius: '3px 3px 0 0' }} />;
            })}
          </div>
        </div>
      </div>

      {toSend.length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '12px 14px 8px', fontSize: 14, fontWeight: 650, color: C.ink, display: 'flex', alignItems: 'center', gap: 7 }}>
            <Icon n="chat" size={15} color={C.accent} /> Send today <span style={{ color: C.muted, fontWeight: 500 }}>· {toSend.length}</span>
          </div>
          {toSend.map((t, i) => {
            const di = t.dueInDays ?? 0; const late = di < 0;
            return (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderTop: i ? `1px solid ${C.line2}` : 0 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ fontSize: 13.3, fontWeight: 600, color: C.ink, display: 'block' }}>{t.tenantName}</b>
                  <small style={{ fontSize: 11.8, color: late ? C.red : C.muted }}>{inr(t.balance ?? 0)} · {late ? `${Math.abs(di)}d overdue` : di === 0 ? 'due today' : `due in ${di}d`}</small>
                </div>
                <button onClick={() => nudge(t)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#128c4b', color: '#fff', border: 0, borderRadius: 9, padding: '8px 13px', fontWeight: 650, fontSize: 12.5, flex: 'none', opacity: t.tenantPhone ? 1 : 0.5 }}>
                  <Icon n="chat" size={14} color="#fff" /> Send
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px 8px' }}>
          <h4 style={{ margin: 0, fontSize: 14, color: C.ink, fontWeight: 650 }}>Upcoming &amp; overdue</h4>
        </div>
        {dates.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px' }}>
            <span style={{ width: 30, height: 30, borderRadius: 999, background: C.greenSoft, color: C.hi, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
              <Icon n="check" size={15} />
            </span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>All clear</div>
              <div style={{ fontSize: 11.8, color: C.muted }}>No upcoming or overdue dates.</div>
            </div>
          </div>
        ) : (
          dates.slice(0, 4).map((d, i) => {
            const dc = dateCount(d);
            return (
              <Row
                key={d.id ?? i}
                icon={d.overdue ? 'bell' : d.type === 'escalation' ? 'cal' : 'doc'}
                tone={d.overdue ? 'rd' : d.type === 'escalation' ? 'br' : 'ac'}
                t={d.title}
                s={d.subtitle ?? `${dc.n} ${dc.unit}`}
                r={d.amountLabel ?? (d.amount != null ? inr(d.amount) : undefined)}
                chev={d.amount == null && !d.amountLabel}
              />
            );
          })
        )}
      </div>
    </>
  );
}

function MobileList({ state, onOpen }: { state: ReturnType<typeof useAsync<Overview>>; onOpen: (id: string) => void }) {
  const [adding, setAdding] = useState(false);
  const tenancies = state.data?.tenancies ?? [];
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 2px 0' }}>
        <b style={{ fontSize: 15, color: C.ink, fontWeight: 650 }}>Tenancies</b>
        <button onClick={() => setAdding(true)} style={addBtn}><Icon n="plus" size={13} color="#fff" /> Add tenancy</button>
      </div>
      {state.loading ? (
        <Loading label="Loading…" minHeight={220} />
      ) : tenancies.length === 0 ? (
        <Empty icon="key" title="No tenancies yet" minHeight={220}>
          <button onClick={() => setAdding(true)} style={retryBtn}><Icon n="plus" size={14} /> Add your first tenancy</button>
        </Empty>
      ) : (
        <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, overflow: 'hidden' }}>
      {tenancies.map((t, i) => {
        const p = rowPill(t.status, t.dueInDays);
        return (
          <div key={t.id} onClick={() => onOpen(t.id)} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '12px 14px', borderTop: i ? `1px solid ${C.line2}` : 0, cursor: 'pointer' }}>
            <span style={{ width: 32, height: 32, borderRadius: 8, background: C.soft, color: C.accent2, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
              <Icon n="key" size={16} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b style={{ fontSize: 13.3, color: C.ink, fontWeight: 600, display: 'block' }}>{t.unitLabel} · {t.tenantName}</b>
              <small style={{ fontSize: 12, color: C.muted }}>{inr(t.rentAmount)}/mo</small>
            </div>
            <Pill kind={p.kind}>{p.label}</Pill>
          </div>
        );
      })}
        </div>
      )}
      {adding && (
        <AddTenancy onClose={() => setAdding(false)} onCreated={() => { setAdding(false); state.reload(); }} />
      )}
    </>
  );
}

type MTab = 'ledger' | 'documents' | 'dates' | 'maintenance';
const MTABS: Array<[MTab, string]> = [
  ['ledger', 'Ledger'],
  ['documents', 'Docs'],
  ['dates', 'Dates'],
  ['maintenance', 'Repairs'],
];

function MobileDetail({ tenancyId }: { tenancyId: string }) {
  const notify = useToast();
  const [sheet, setSheet] = useState<null | 'payment' | 'edit' | 'invite'>(null);
  const [mtab, setMtab] = useState<MTab>('ledger');
  const [scope, setScope] = useState<LedgerScope>({}); // {} → current FY (default)
  const { data, loading, error, reload } = useAsync<{ detail: TenancyDetail | null; ledger: TenancyLedger | null }>(async () => {
    const [detail, ledger] = await Promise.all([
      api.tenancy(tenancyId).catch(() => null),
      api.ledger(tenancyId, scope).catch(() => null),
    ]);
    return { detail, ledger };
  }, [tenancyId, scope]);

  if (loading && !data?.ledger) return <Loading label="Loading ledger…" minHeight={300} />;
  if (error || !data?.ledger)
    return (
      <Empty icon="ledger" title="Ledger unavailable" minHeight={300}>
        <button onClick={reload} style={retryBtn}>Retry</button>
      </Empty>
    );

  const tenancy = data.detail?.tenancy ?? data.ledger.tenancy ?? null;
  const advanced = !!tenancy?.escalationEnabled;
  const tdsRate = tenancy ? Math.round(tenancy.tdsRateBps / 100) : 10;
  const ledger = data.ledger;
  const register = buildRegister({
    periods: ledger.periods,
    payments: ledger.payments,
    openingBalance: ledger.openingBalance,
    tdsRatePct: tdsRate,
  });

  async function toggle() {
    if (!tenancy) return;
    try {
      await api.updateTenancy(tenancyId, { escalationEnabled: !advanced });
      notify(!advanced ? 'Advanced lines on' : 'Advanced lines off');
      reload();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not update');
    }
  }
  async function remind() {
    try {
      await api.sendReminder(tenancyId);
      notify(`Reminder sent to ${data?.detail?.tenantName ?? 'tenant'}`);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not send reminder');
    }
  }

  return (
    <>
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: '12px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h4 style={{ margin: 0, fontSize: 14, color: C.ink, fontWeight: 650 }}>{data.detail?.tenantName ?? 'Tenancy'}</h4>
          {ledger.closingBalance > 0 ? <Pill kind="over">{inr(ledger.closingBalance)} due</Pill> : <Pill kind="paid">Clear</Pill>}
        </div>
        {tenancy && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            {[`Rent ${inr(tenancy.rentAmount)}/mo`, advanced ? 'Advanced' : tenancy.tdsEnabled ? 'Simple + TDS' : 'Simple'].map((c, i) => (
              <span key={i} style={{ fontSize: 11.6, fontWeight: 600, padding: '5px 9px', borderRadius: 999, border: `1px solid ${C.line}`, color: C.ink2, background: C.card }}>{c}</span>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.3, color: C.muted, marginTop: 12 }}>
          <span>Advanced lines</span>
          <button onClick={toggle} aria-label="toggle advanced" style={{ width: 32, height: 18, borderRadius: 999, background: advanced ? C.accent : '#cdd3cc', position: 'relative', border: 0, cursor: 'pointer', flex: 'none' }}>
            <span style={{ position: 'absolute', top: 2, left: advanced ? 16 : 2, width: 14, height: 14, borderRadius: 999, background: '#fff', transition: 'left .15s' }} />
          </button>
          <b style={{ color: C.ink }}>{advanced ? 'on' : 'off'}</b>
        </div>
      </div>

      {/* per-tenancy tabs */}
      <div style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${C.line}` }}>
        {MTABS.map(([key, label]) => {
          const on = mtab === key;
          return (
            <button
              key={key}
              onClick={() => setMtab(key)}
              style={{ flex: 1, padding: '9px 4px', fontSize: 12.6, fontWeight: 650, color: on ? C.accent : C.muted, background: 'transparent', border: 0, borderBottom: `2px solid ${on ? C.accent : 'transparent'}`, marginBottom: -1, cursor: 'pointer' }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {mtab === 'documents' ? (
        <TenancyDocuments tenancyId={tenancyId} tenantName={data.detail?.tenantName} />
      ) : mtab === 'dates' ? (
        <TenancyDates tenancyId={tenancyId} />
      ) : mtab === 'maintenance' ? (
        <TenancyRequests tenancyId={tenancyId} />
      ) : (
        <>
          {ledger.window && ledger.window.fyOptions.length > 0 && (
            <LedgerFilter fyOptions={ledger.window.fyOptions} value={scope} onChange={setScope} />
          )}
          {register.length === 0 ? (
            <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: '14px', color: C.muted, fontSize: 12.5 }}>No entries in this period.</div>
          ) : (
            <Register entries={register} compact />
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11.8, color: C.muted }}>
            <span>Net of {tdsRate}% TDS the tenant deducts</span>
            <span style={{ ...mono() }}>Balance · <b style={{ color: ledger.closingBalance > 0 ? C.red : C.hi }}>{inr(ledger.closingBalance)}</b></span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={remind} style={{ flex: 1, background: C.accent, color: '#fff', border: 0, borderRadius: 11, padding: 12, fontWeight: 650, fontSize: 13.5, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
              <Icon n="chat" size={15} color="#fff" /> Send reminder
            </button>
            <button onClick={() => window.open(api.caExportUrl(tenancyId), '_blank', 'noopener')} aria-label="export" style={{ flex: 'none', background: C.card, color: C.accent, border: `1px solid ${C.line}`, borderRadius: 11, padding: '12px 15px', cursor: 'pointer' }}>
              <Icon n="doc" size={16} />
            </button>
          </div>
        </>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <button onClick={() => setSheet('payment')} style={secBtn}>Record payment</button>
        <button onClick={() => setSheet('edit')} style={secBtn}>Edit terms</button>
        <button onClick={() => setSheet('invite')} style={secBtn}>Invite</button>
      </div>

      {sheet === 'payment' && (
        <RecordPayment tenancyId={tenancyId} tenantName={data.detail?.tenantName} onClose={() => setSheet(null)} onRecorded={() => { setSheet(null); reload(); }} />
      )}
      {sheet === 'edit' && tenancy && (
        <EditTenancy tenancy={tenancy} tenantName={data.detail?.tenantName} onClose={() => setSheet(null)} onSaved={() => { setSheet(null); reload(); }} />
      )}
      {sheet === 'invite' && tenancy && (
        <InviteTenant partyId={tenancy.tenantPartyId} tenantName={data.detail?.tenantName} onClose={() => setSheet(null)} onSaved={() => { setSheet(null); reload(); }} />
      )}
    </>
  );
}

const secBtn: React.CSSProperties = {
  border: `1px solid ${C.line}`,
  background: C.card,
  color: C.accent,
  fontWeight: 600,
  fontSize: 12.5,
  padding: '10px 6px',
  borderRadius: 10,
  cursor: 'pointer',
};

const addBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  border: 0,
  background: C.accent,
  color: '#fff',
  fontWeight: 650,
  fontSize: 12.5,
  padding: '8px 12px',
  borderRadius: 999,
  cursor: 'pointer',
};

const retryBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: `1px solid ${C.line}`,
  background: C.card,
  color: C.accent,
  fontWeight: 600,
  fontSize: 13,
  padding: '8px 16px',
  borderRadius: 9,
  cursor: 'pointer',
};
